import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowRight,
  Expand,
  Focus,
  ImageOff,
  Minus,
  Plus,
  RefreshCw
} from 'lucide-react';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import {
  cleanRenderedSvg,
  detectMermaidTheme,
  loadMermaid,
  prepareMermaidCode,
  type MermaidDirection
} from '../../components/mermaid-runtime';
import { useGenerateDiagram } from '../../api/queries';
import { useAppParams } from '../../api/params';
import { getFileUrl } from '../../api/client';
import styles from './DiagramView.module.css';

type RenderMode = 'svg' | 'png' | 'error';
type Direction = MermaidDirection;

// Round zoom levels used by the +/− buttons. The mouse wheel keeps continuous
// zoom for finer control.
const ZOOM_SNAPS = [0.15, 0.25, 0.5, 0.75, 0.9, 1.0, 1.1, 1.25, 1.5, 2.0, 3.0, 4.0];

export function DiagramView() {
  const mut = useGenerateDiagram();
  const params = useAppParams();
  const [svg, setSvg] = useState<string>('');
  const [mode, setMode] = useState<RenderMode>('svg');
  const [renderError, setRenderError] = useState<string>('');
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [direction, setDirection] = useState<Direction>('LR');
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const code: string = useMemo(
    () => (mut.data as any)?.content?.mermaid_code ?? '',
    [mut.data]
  );

  // Render mermaid → SVG
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        const theme = detectMermaidTheme(viewportRef.current);
        const mermaid = await loadMermaid(theme);
        const id = 'phia-diagram-' + Date.now();
        const prepared = prepareMermaidCode(code, { direction });
        const { svg: rendered } = await mermaid.render(id, prepared);
        if (cancelled) return;
        setSvg(cleanRenderedSvg(rendered));
        setMode('svg');
      } catch (err) {
        if (!cancelled) {
          setRenderError(err instanceof Error ? err.message : 'render error');
          setMode((m) => (m === 'svg' ? 'error' : m));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, direction]);

  // Pan handlers (pointer events so trackpad + touch both work)
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y
    };
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setOffset({
      x: dragRef.current.baseX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.baseY + (e.clientY - dragRef.current.startY)
    });
  };
  const endPan = (e: React.PointerEvent) => {
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
    setDragging(false);
  };

  // Zoom (wheel + buttons)
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 50) return;
      e.preventDefault();
      const delta = -e.deltaY * 0.0015;
      setScale((s) => clamp(s * (1 + delta), 0.3, 4));
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, []);

  const zoomIn = () =>
    setScale((s) => ZOOM_SNAPS.find((l) => l > s + 0.001) ?? s);
  const zoomOut = () =>
    setScale((s) => [...ZOOM_SNAPS].reverse().find((l) => l < s - 0.001) ?? s);

  /**
   * Baseline view: fit the entire diagram into the viewport (everything
   * visible, even at small scale — the user can zoom in from there) and
   * anchor by the start of the flow direction.
   *
   *   LR → left edge of the content sits against the left padding,
   *        vertically centered.
   *   TD → top edge of the content sits against the top padding,
   *        horizontally centered.
   *
   * This ensures the root node is always at the start of the viewport
   * regardless of how much empty space the non-limiting axis has.
   */
  // Reset to 100% zoom at origin. Let the user pan/zoom from there.
  const fit = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  // Auto-fit whenever a new SVG mounts.
  useEffect(() => {
    if (mode !== 'svg' || !svg) return;
    const t = window.setTimeout(fit, 30);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svg, mode]);

  if (mut.isPending)
    return <LoadingState label="Construyendo diagrama…" hint="Analizando relaciones del módulo" />;
  if (mut.error)
    return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;

  const title: string = data.content?.title ?? data.title ?? '';
  const desc: string = data.content?.description ?? '';
  const pngFile: string = data.content?.file ?? '';
  const pngUrl = pngFile ? getFileUrl(pngFile, params) : '';

  return (
    <div className={styles.view}>
      <header className={styles.head}>
        {title && <h2 className={styles.title}>{title}</h2>}
        {desc && <p className={styles.desc}>{desc}</p>}
      </header>

      <div className={styles.canvas}>
        <div className={styles.toolbar} role="toolbar" aria-label="Controles del diagrama">
          <button type="button" className={styles.tool} onClick={zoomOut} aria-label="Alejar">
            <Minus size={14} strokeWidth={2.2} />
          </button>
          <span className={styles.scale} aria-live="polite">{Math.round(scale * 100)}%</span>
          <button type="button" className={styles.tool} onClick={zoomIn} aria-label="Acercar">
            <Plus size={14} strokeWidth={2.2} />
          </button>
          <span className={styles.toolDivider} />
          <button type="button" className={styles.tool} onClick={fit} aria-label="Centrar" title="Centrar">
            <Focus size={14} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            className={styles.tool}
            onClick={() => setDirection((d) => (d === 'LR' ? 'TD' : 'LR'))}
            aria-label={direction === 'LR' ? 'Cambiar a vertical' : 'Cambiar a horizontal'}
            title={direction === 'LR' ? 'Vertical' : 'Horizontal'}
          >
            {direction === 'LR'
              ? <ArrowRight size={14} strokeWidth={2.2} />
              : <ArrowDown size={14} strokeWidth={2.2} />}
          </button>
          {pngUrl && mode === 'svg' && (
            <button
              type="button"
              className={styles.tool}
              onClick={() => setMode('png')}
              aria-label="Ver versión PNG"
              title="Ver como imagen"
            >
              <ImageOff size={14} strokeWidth={2.2} />
            </button>
          )}
          {mode === 'png' && (
            <button
              type="button"
              className={styles.tool}
              onClick={() => setMode('svg')}
              aria-label="Volver al SVG"
              title="Volver al SVG"
            >
              <RefreshCw size={14} strokeWidth={2.2} />
            </button>
          )}
        </div>

        <div
          className={styles.viewport}
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPan}
          onPointerCancel={endPan}
          onDoubleClick={fit}
          data-dragging={dragging || undefined}
        >
          <div
            ref={stageRef}
            className={styles.stage}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
          >
            {mode === 'svg' && svg && (
              <div className={styles.svgHolder} dangerouslySetInnerHTML={{ __html: svg }} />
            )}
            {mode === 'png' && pngUrl && (
              <img src={pngUrl} alt={title || 'Diagrama'} className={styles.png} draggable={false} />
            )}
            {mode === 'svg' && !svg && !renderError && (
              <div className={styles.placeholder}>
                <LoadingState label="Renderizando…" />
              </div>
            )}
            {mode === 'error' && (
              <div className={styles.errorBox}>
                <p>El SVG no pudo renderizar.</p>
                {pngUrl && (
                  <button type="button" className={styles.errorBtn} onClick={() => setMode('png')}>
                    <Expand size={14} /> Ver versión generada por el servidor
                  </button>
                )}
                <details>
                  <summary>Detalles</summary>
                  <pre>{renderError || 'Error desconocido'}</pre>
                </details>
              </div>
            )}
          </div>
        </div>

        <p className={styles.hint}>
          <span>Arrastra para mover · scroll/cmd+scroll para zoom · doble clic para encajar</span>
        </p>
      </div>
    </div>
  );
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}
